import { computed, effect, signal } from '@preact/signals';
import type { AppServices } from '../app';
import { type HijriMonth, hijriName, monthKey } from '../astro/hijri';
import { enuToAltAz, refractEnu, type Vec3 } from '../lib/math';
import { STORE, Selection, fovDeg, loadJSON, place, saveJSON, settings, showToast, simNow } from '../state';
import { clouds, loadClouds } from './conditions';
import { logKey } from './logbook';
import { displayName } from './satellites';
import {
  Night, NightPlace, Nights, Sighting, SightingKind, cleanNights, firstSeen, fromLogbook, mergeNights, newId, nightId, nightLabel,
  withSighting, withoutSighting,
} from './nightsCore';

// Your nights: what has been seen, night by night, kept on this device only (nothing is sent anywhere).
// "Seen it" on a card adds to the night of the time on show; "Save this night" keeps the moment itself.

export const hereNow = (): NightPlace => {
  const p = place.peek();
  return { name: p.name, lat: p.lat, lon: p.lon, height: p.height, ...(p.timeZone ? { timeZone: p.timeZone } : {}) };
};

function initial(): Nights {
  const saved = loadJSON<unknown>('nights', null);
  if (saved !== null) return cleanNights(saved);
  // The first time: bring over the "Seen it" log kept before nights were.
  const log = loadJSON<Record<string, { t: number; place: string }>>('logbook', {});
  return Object.keys(log).length ? fromLogbook(log, hereNow()) : {};
}

export const nights = signal<Nights>(initial());

/** Why the phone did not keep a change to Your nights: its storage for Ahillah is full, or the browser will not let
 *  Ahillah save at all (website data switched off). */
export type Unsaved = 'full' | 'blocked';
const UNSAVED: Record<Unsaved, string> = {
  full: 'Your phone’s storage for Ahillah is full, so this night could not be saved. Back up your nights in Settings.',
  blocked: 'Ahillah is not allowed to save on this phone, so this night could not be saved. Check that your browser allows websites to save data.',
};
/** The nights as the phone has them stored (null until it first has). */
let stored: Nights | null = null;
/** Store the nights on the phone: null once they are there, else why it would not take them. */
function store(v: Nights): Unsaved | null {
  if (v === stored) return null;
  try {
    localStorage.setItem(`${STORE}nights`, JSON.stringify(v));
  } catch (e) {
    return (e as Error)?.name === 'QuotaExceededError' ? 'full' : 'blocked';
  }
  stored = v;
  return null;
}
/** Keep `next` as the nights: stored first, then shown. If the phone will not store it, the nights stay as they were;
 *  why not, or null once kept. */
function commit(next: Nights): Unsaved | null {
  const why = store(next);
  if (!why) nights.value = next;
  return why;
}
/** The same, saying why when the phone would not keep it: whether it was kept. Each try that fails says so again. */
function save(next: Nights): boolean {
  const why = commit(next);
  if (why) showToast(UNSAVED[why], 8000);
  return !why;
}
// Nights set from outside (an Undo) are stored as they are set. Nothing the phone would not store stays on show: the
// nights go back to what it has, and the person is told why.
effect(() => {
  const why = store(nights.value);
  if (!why || !stored) return;
  nights.value = stored;
  showToast(UNSAVED[why], 8000);
});

// Another window of Ahillah on this device (the browser and the installed app share one store): take in
// what it saved, so neither writes over the other's nights.
try {
  addEventListener('storage', (e) => {
    if (e.key !== `${STORE}nights` || !e.newValue) return;
    try {
      nights.value = cleanNights(JSON.parse(e.newValue));
    } catch {
      /* not readable: keep ours */
    }
  });
} catch {
  /* no window (tests) */
}

/** Everything ever seen, by key: its first sighting and how many nights it was seen on. */
export const seenIndex = computed(() => firstSeen(nights.value));

/** The night the time on show belongs to, where the sky is shown from. */
export const nightNow = () => nightId(simNow().getTime(), place.peek().lon);

const round1 = (x: number) => Math.round(x * 10) / 10;
const altAz = (v: Vec3): [number, number] => {
  const a = enuToAltAz(refractEnu(v));
  return [round1(a.alt), round1(a.az)];
};

function kindOf(sel: Selection): SightingKind | null {
  switch (sel.kind) {
    case 'body': case 'star': case 'dso': case 'constellation': case 'satellite': case 'shower': case 'small': return sel.kind;
    default: return null;
  }
}

/** The forecast's cloud and temperature for the hour, when it is to hand for that place. */
function weatherAt(t: number, p: NightPlace): Night['weather'] | undefined {
  const c = clouds.peek();
  if (!c || c.key !== `${p.lat.toFixed(2)},${p.lon.toFixed(2)}` || !c.hours.length) return undefined;
  const h = c.hours.reduce((b, x) => (Math.abs(x.t - t) < Math.abs(b.t - t) ? x : b), c.hours[0]);
  if (Math.abs(h.t - t) > 90 * 60e3) return undefined;
  const w: Night['weather'] = { cloud: Math.round(h.total) };
  if (typeof h.temp === 'number') w.tempC = Math.round(h.temp);
  return w;
}

/** A night with the conditions of its first moment filled in, where they are not yet. */
function withConditions(ns: Nights, id: string, t: number, p: NightPlace): Nights {
  const n = ns[id];
  if (!n) return ns;
  const set = settings.peek();
  const sky = n.sky ?? { bortle: set.bortle, sqm: set.sqm };
  const weather = n.weather ?? weatherAt(t, p);
  if (!weather && Math.abs(t - Date.now()) < 3 * 3600e3) {
    // Not loaded yet: fetch it, then fill it in.
    void loadClouds(place.peek()).then(() => {
      const cur = nights.peek()[id];
      const w = cur && !cur.weather ? weatherAt(t, p) : undefined;
      // Filled in by itself: if the phone will not store it, the night goes without.
      if (w) commit({ ...nights.peek(), [id]: { ...cur, weather: w } });
    });
  }
  return { ...ns, [id]: { ...n, sky, ...(weather ? { weather } : {}) } };
}

/** Change one sighting in place (its saved orbit arrives after it is added; if the phone will not store it, it goes
 *  without). */
function patchSighting(night: string, id: string, f: (s: Sighting) => Partial<Sighting> | null) {
  const n = nights.peek()[night];
  const i = n?.sightings.findIndex((s) => s.id === id) ?? -1;
  if (!n || i < 0) return;
  const change = f(n.sightings[i]);
  if (!change) return;
  const sightings = n.sightings.slice();
  sightings[i] = { ...sightings[i], ...change };
  commit({ ...nights.peek(), [night]: { ...n, sightings } });
}

// ---------- Adding and removing ----------
/** Once: ask the browser to keep Ahillah's storage, so a phone short of space does not clear saved nights. */
function keepStorage() {
  if (loadJSON<{ v?: number }>('persistAsked', {}).v) return;
  saveJSON('persistAsked', { v: 1 });
  void (navigator.storage?.persist?.() ?? Promise.resolve()).catch(() => {});
}

function announce(night: string, first: boolean) {
  keepStorage();
  const tonight = night === nightId(Date.now(), place.peek().lon);
  const where = tonight ? 'tonight' : `the night of ${nightLabel(night, false)}`;
  if (first && !loadJSON<{ v?: number }>('nightsHint', {}).v) {
    saveJSON('nightsHint', { v: 1 });
    showToast(`Added to ${where}. Find it in Tonight › Your nights.`, 5000);
  } else showToast(`Added to ${where}`);
}

/**
 * Tick something as seen at the time on show (or at `t`): it joins that night, with what cannot be
 * looked up later (a satellite's orbit data and its pass). Whether it was added (and stored on the phone).
 */
export function seeIt(s: AppServices, sel: Selection, t = simNow().getTime()): boolean {
  const key = logKey(s, sel);
  const kind = kindOf(sel);
  if (!key || !kind) return false;
  const pl = hereNow();
  const loc = s.engine.locate(sel);
  const x: Sighting = { id: newId(t), key, name: s.engine.labelFor(sel), kind, t, place: pl };
  if (loc) x.at = altAz(loc.enu);
  const first = !Object.keys(nights.peek()).length;
  const r = withSighting(nights.peek(), x);
  if (!r.added || !save(withConditions(r.nights, r.night, t, pl))) return false;
  announce(r.night, first);
  if (sel.kind === 'satellite') void keepOrbit(s, sel.id, r.night, x.id, t);
  return true;
}

/** The key of the new crescent of a Hijri month in Your nights. */
export const crescentKey = (m: HijriMonth) => `crescent:${monthKey(m)}`;

/**
 * "I saw the crescent": the family's own record of the new crescent of `month`, on tonight's night, where the Moon was.
 * Kept on this phone only, like everything in Your nights. Whether it was added (it is kept once a night, and only once
 * the phone has stored it).
 */
export function sawCrescent(s: AppServices, month: HijriMonth, t = Date.now()): boolean {
  const pl = hereNow();
  const x: Sighting = { id: newId(t), key: crescentKey(month), name: `New crescent (hilāl) of ${hijriName(month)}`, kind: 'crescent', t, place: pl };
  const loc = s.engine.locate({ kind: 'body', id: 'Moon' });
  if (loc) x.at = altAz(loc.enu);
  const first = !Object.keys(nights.peek()).length;
  const r = withSighting(nights.peek(), x);
  if (!r.added || !save(withConditions(r.nights, r.night, t, pl))) return false;
  announce(r.night, first);
  return true;
}

/** A satellite's orbit data then, and its pass as seen (four minutes either side, every 10 s). */
async function keepOrbit(s: AppServices, id: number, night: string, sid: string, t: number) {
  const [orbit, tr] = await Promise.all([s.sats.orbit(id).catch(() => null), s.sats.tracks(new Date(t - 240e3), new Date(t + 240e3), 10, 99, [id]).catch(() => null)]);
  const pass: [number, number][] = [];
  const pts = tr?.result.find((r) => r.id === id)?.pts;
  // Where it was at the moment itself.
  let at: [number, number] | null = null, gap = Infinity;
  if (pts && tr) {
    for (let k = 0; k * 5 < pts.length; k++) {
      const o = k * 5;
      const v: Vec3 = [pts[o], pts[o + 1], pts[o + 2]];
      if (pts[o + 2] > 0 && pts[o + 4] > 0.5) pass.push(altAz(v));
      const d = Math.abs(tr.times[k] - t);
      if (pts[o + 2] > -0.05 && d < gap) {
        gap = d;
        at = altAz(v);
      }
    }
  }
  patchSighting(night, sid, () => ({ ...(orbit ? { orbit } : {}), ...(pass.length > 1 ? { pass } : {}), ...(at && gap < 30e3 ? { at } : {}) }));
}

/** Whether something is ticked on the night of the time on show, and that sighting. */
export function seenOnNight(key: string, night = nightNow()): Sighting | null {
  return nights.value[night]?.sightings.find((x) => x.key === key) ?? null;
}

/** Take a sighting off its night; whether that was stored. */
export function unsee(night: string, sightingId: string): boolean {
  return save(withoutSighting(nights.peek(), night, sightingId));
}

/** "Save this night": keep the moment on show, where the view is looking, even with nothing ticked. The night's id, or
 *  null if the phone would not store it. */
export function keepNight(s: AppServices): string | null {
  const t = simNow().getTime();
  const pl = hereNow();
  const id = nightId(t, pl.lon);
  const c = s.engine.viewCentre();
  const kept = { t, at: [round1(c.alt), round1(c.az)] as [number, number], fov: Math.max(0.05, Math.round(fovDeg.peek() * 100) / 100) };
  const cur = nights.peek()[id];
  const first = !Object.keys(nights.peek()).length;
  const n: Night = cur ? { ...cur, kept, updated: Date.now() } : { id, place: pl, sightings: [], note: '', kept, updated: Date.now() };
  if (!save(withConditions({ ...nights.peek(), [id]: n }, id, t, pl))) return null;
  void first;
  keepStorage();
  return id;
}

export function setNote(id: string, note: string) {
  const n = nights.peek()[id];
  if (n && n.note !== note) save({ ...nights.peek(), [id]: { ...n, note: note.slice(0, 4000), updated: Date.now() } });
}

/** Delete a night; whether that was stored. */
export function deleteNight(id: string): boolean {
  const next = { ...nights.peek() };
  delete next[id];
  return save(next);
}

// ---------- Backup ----------
export function backupText(): string {
  const list = Object.values(nights.peek()).sort((a, b) => a.id.localeCompare(b.id));
  return JSON.stringify({ app: 'Ahillah', kind: 'nights', version: 1, exported: new Date().toISOString(), nights: list });
}

/** Bring nights in from a backup file's text (one made under the app's first name too); null if it holds no nights.
 *  `unsaved`: why the phone would not store them, in which case nothing is restored and what was here stays. */
export function restoreFrom(text: string): { nightsAdded: number; sightingsAdded: number; unsaved: Unsaved | null } | null {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return null;
  }
  const list = raw && typeof raw === 'object' && 'nights' in (raw as object) ? (raw as { nights: unknown }).nights : raw;
  const incoming = cleanNights(list);
  if (!Object.keys(incoming).length) return null;
  const r = mergeNights(nights.peek(), incoming);
  // Restored only once the phone has stored it all.
  const unsaved = commit(r.nights);
  return unsaved ? { nightsAdded: 0, sightingsAdded: 0, unsaved } : { nightsAdded: r.nightsAdded, sightingsAdded: r.sightingsAdded, unsaved };
}

// ---------- From keys back to things in the sky ----------
/** The thing a sighting's key names, as the sky knows it now (null for shooting stars, and for planes, which older nights may hold). */
export function keyToSelection(s: AppServices, key: string): Selection | null {
  const i = key.indexOf(':');
  const kind = key.slice(0, i), id = key.slice(i + 1);
  const e = s.engine;
  switch (kind) {
    case 'body': return { kind: 'body', id };
    case 'star': {
      const k = e.stars.byHip.get(Number(id));
      return k === undefined ? null : { kind: 'star', index: k };
    }
    case 'dso': {
      const k = e.dsos.findIndex((d) => d.id === id);
      return k < 0 ? null : { kind: 'dso', index: k };
    }
    case 'con': {
      const k = e.cons.constellations.findIndex((c) => c.id === id);
      return k < 0 ? null : { kind: 'constellation', index: k };
    }
    case 'sat': return Number(id) ? { kind: 'satellite', id: Number(id) } : null;
    case 'shower': return { kind: 'shower', code: id };
    case 'small': return { kind: 'small', name: id };
    case 'crescent': return { kind: 'body', id: 'Moon' };
    default: return null;
  }
}

/** The kinds Ahillah names from its own catalogues, which can be renamed (a nickname dropped, BUILD-SPEC item 32). */
const CATALOGUED = new Set<SightingKind>(['star', 'dso', 'constellation']);

/** A sighting's name: as the sky names it now, for a star, cluster or constellation; otherwise as it was shown, or (for
 * ones brought over from the old log) as the sky names it. */
export function sightingName(s: AppServices | null, x: Sighting): string {
  if (x.name && !(s && CATALOGUED.has(x.kind))) return x.name;
  const id = x.key.slice(x.key.indexOf(':') + 1);
  if (s && x.kind === 'satellite') {
    const m = s.sats.meta.get(Number(id));
    if (m) return displayName(m);
  }
  if (s && x.kind === 'shower') {
    const sh = s.meteors.showers.find((w) => w.code === id);
    if (sh) return sh.name;
  }
  const sel = s ? keyToSelection(s, x.key) : null;
  if (s && sel) {
    try {
      const l = s.engine.labelFor(sel);
      if (l) return l;
    } catch {
      /* not known to the sky now */
    }
  }
  return x.name || x.key.slice(x.key.indexOf(':') + 1);
}
